import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "./NavBar";

const API_BASE_URL = "https://api.itrustsecure.com";
function getToken() { return localStorage.getItem("itrust_token"); }
function authHeaders() { return { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` }; }

const NOTIF_TYPES = [
  { key: "newMessage", label: "New Messages", icon: "💬", desc: "When someone sends you a message" },
  { key: "newOrder", label: "New Orders", icon: "🛒", desc: "When a buyer places an order" },
  { key: "orderStatus", label: "Order Updates", icon: "📦", desc: "When your order status changes" },
  { key: "newOffer", label: "New Offers", icon: "🤝", desc: "When someone makes you an offer" },
  { key: "priceProposal", label: "Price Proposals", icon: "💰", desc: "When someone proposes a price" },
  { key: "disputeFiled", label: "Disputes", icon: "⚠️", desc: "When a dispute is filed" },
];

const NOTIF_CHANNELS = [
  { key: "channelPush", label: "Push Notifications", icon: "📱", desc: "Notifications on your device" },
  { key: "channelEmail", label: "Email", icon: "📧", desc: "Notifications to your email" },
  { key: "channelSms", label: "SMS", icon: "📲", desc: "Text message notifications" },
];

function NotificationPreferences() {
  const [prefs, setPrefs] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    fetch(`${API_BASE_URL}/notifications/preferences`, { headers: authHeaders() })
      .then(r => r.json()).then(setPrefs).catch(console.warn);
  }, []);

  async function toggle(key) {
    if (!prefs) return;
    const updated = { ...prefs, [key]: !prefs[key] };
    setPrefs(updated);
    setSaving(true);
    try {
      await fetch(`${API_BASE_URL}/notifications/preferences`, { method: "PUT", headers: authHeaders(), body: JSON.stringify({ [key]: !prefs[key] }) });
    } catch { } finally { setSaving(false); }
  }

  const rowStyle = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0", borderBottom: "1px solid rgba(59,130,246,0.08)" };
  const lastRowStyle = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0" };
  const labelStyle = { display: "flex", alignItems: "center", gap: 10 };
  const cardStyle = { background: "rgba(255,255,255,0.75)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 16, padding: 24, marginBottom: 16, backdropFilter: "blur(8px)", boxShadow: "0 2px 12px rgba(59,130,246,0.08)" };

  if (!prefs) return <div style={{ padding: 20, color: "#94a3b8", textAlign: "center" }}>Loading preferences...</div>;

  return (
    <>
      {saving && <div style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#3b82f6" }}>Saving...</div>}
      <div style={cardStyle}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4, color: "#1e293b" }}>Notification Types</div>
        <div style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>Choose what events you want to be notified about</div>
        {NOTIF_TYPES.map((t, i) => (
          <div key={t.key} style={i < NOTIF_TYPES.length - 1 ? rowStyle : lastRowStyle}>
            <div style={labelStyle}>
              <span style={{ fontSize: 20, width: 28 }}>{t.icon}</span>
              <div><div style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{t.label}</div><div style={{ fontSize: 12, color: "#64748b" }}>{t.desc}</div></div>
            </div>
            <input type="checkbox" checked={prefs[t.key] ?? true} onChange={() => toggle(t.key)} style={{ width: 18, height: 18, cursor: "pointer", accentColor: "#3b82f6" }} />
          </div>
        ))}
      </div>
      <div style={cardStyle}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4, color: "#1e293b" }}>Notification Channels</div>
        <div style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>Choose how you want to receive notifications</div>
        {NOTIF_CHANNELS.map((c, i) => (
          <div key={c.key} style={i < NOTIF_CHANNELS.length - 1 ? rowStyle : lastRowStyle}>
            <div style={labelStyle}>
              <span style={{ fontSize: 20, width: 28 }}>{c.icon}</span>
              <div><div style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{c.label}</div><div style={{ fontSize: 12, color: "#64748b" }}>{c.desc}</div></div>
            </div>
            <input type="checkbox" checked={prefs[c.key] ?? false} onChange={() => toggle(c.key)} style={{ width: 18, height: 18, cursor: "pointer", accentColor: "#3b82f6" }} />
          </div>
        ))}
      </div>
    </>
  );
}

export default function ProfilePage({ user, onLogout }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("profile");
  const [saving, setSaving] = useState(false);
  const [fullName, setFullName] = useState(user?.fullName || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  function handleSignOut() {
    if (!window.confirm("Are you sure you want to sign out?")) return;
    onLogout(); navigate("/");
  }

  async function handleSaveProfile(e) {
    e.preventDefault(); setMessage(""); setError(""); setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/users/${user.id}`, { method: "PUT", headers: authHeaders(), body: JSON.stringify({ fullName, phone }) });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Could not update profile"); return; }
      localStorage.setItem("itrust_user", JSON.stringify({ ...user, fullName, phone }));
      setMessage("Profile updated successfully!");
    } catch { setError("Something went wrong"); }
    finally { setSaving(false); }
  }

  async function handleChangePassword(e) {
    e.preventDefault(); setMessage(""); setError("");
    if (newPassword !== confirmPassword) { setError("Passwords do not match"); return; }
    if (newPassword.length < 8) { setError("Password must be at least 8 characters"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/users/${user.id}/password`, { method: "PUT", headers: authHeaders(), body: JSON.stringify({ currentPassword, newPassword }) });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Could not change password"); return; }
      setMessage("Password changed successfully!");
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch { setError("Something went wrong"); }
    finally { setSaving(false); }
  }

  const styles = {
    root: { fontFamily: "'Segoe UI', Arial, sans-serif", background: "transparent", color: "#0f172a", minHeight: "100vh" },
    main: { maxWidth: 800, margin: "0 auto", padding: "32px 20px" },
    avatarSection: { display: "flex", alignItems: "center", gap: 24, marginBottom: 32, padding: "24px 28px", background: "rgba(255,255,255,0.75)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 20, backdropFilter: "blur(8px)", boxShadow: "0 2px 12px rgba(59,130,246,0.08)" },
    avatar: { width: 80, height: 80, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #7c3aed)", border: "3px solid rgba(59,130,246,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32, fontWeight: 800, color: "#fff", flexShrink: 0 },
    userName: { fontSize: 24, fontWeight: 800, marginBottom: 4, color: "#0f172a" },
    userEmail: { fontSize: 14, color: "#64748b", marginBottom: 10 },
    roleBadge: { display: "inline-block", background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.25)", borderRadius: 999, padding: "4px 14px", fontSize: 12, fontWeight: 700, color: "#3b82f6" },
    tabs: { display: "flex", gap: 8, marginBottom: 24, flexWrap: "wrap" },
    tabBtn: (active) => ({ padding: "10px 20px", borderRadius: 10, border: "1px solid", borderColor: active ? "#3b82f6" : "rgba(59,130,246,0.15)", background: active ? "#3b82f6" : "rgba(255,255,255,0.7)", color: active ? "#fff" : "#475569", cursor: "pointer", fontWeight: 700, fontSize: 13 }),
    card: { background: "rgba(255,255,255,0.75)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 16, padding: 24, marginBottom: 16, backdropFilter: "blur(8px)", boxShadow: "0 2px 12px rgba(59,130,246,0.08)" },
    cardTitle: { fontSize: 16, fontWeight: 700, marginBottom: 20, color: "#1e293b" },
    label: { fontSize: 13, fontWeight: 600, color: "#475569", marginBottom: 6, display: "block" },
    input: { width: "100%", padding: "12px 14px", background: "rgba(255,255,255,0.8)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, color: "#0f172a", fontSize: 14, marginBottom: 16, boxSizing: "border-box", outline: "none" },
    saveBtn: { background: "#3b82f6", color: "#fff", border: "none", padding: "12px 24px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 14, opacity: saving ? 0.5 : 1, boxShadow: "0 4px 12px rgba(59,130,246,0.25)" },
    successMsg: { background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.25)", color: "#16a34a", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16 },
    errorMsg: { background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", color: "#dc2626", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16 },
    infoRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0", borderBottom: "1px solid rgba(59,130,246,0.08)" },
    infoLabel: { fontSize: 14, color: "#64748b" },
    infoValue: { fontSize: 14, fontWeight: 600, color: "#1e293b" },
    signOutBtn: { background: "rgba(220,38,38,0.08)", color: "#dc2626", border: "1px solid rgba(220,38,38,0.25)", padding: "12px 24px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 14, width: "100%" },
    quickAction: (color) => ({ display: "flex", alignItems: "center", gap: 14, padding: 16, background: `${color}08`, border: `1px solid ${color}20`, borderRadius: 14, cursor: "pointer", marginBottom: 10 }),
    quickActionIcon: { fontSize: 24 },
    quickActionTitle: (color) => ({ fontSize: 15, fontWeight: 700, color, marginBottom: 2 }),
    quickActionDesc: { fontSize: 12, color: "#64748b" },
  };

  return (
    <div style={styles.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={styles.main}>
        <div style={styles.avatarSection}>
          <div style={styles.avatar}>{user?.fullName?.charAt(0)?.toUpperCase() || "?"}</div>
          <div>
            <div style={styles.userName}>{user?.fullName}</div>
            <div style={styles.userEmail}>{user?.email}</div>
            <span style={styles.roleBadge}>{user?.role || "USER"}</span>
          </div>
        </div>

        <div style={styles.tabs}>
          {["profile", "security", "notifications", "platform"].map(t => (
            <button key={t} style={styles.tabBtn(tab === t)} onClick={() => { setTab(t); setMessage(""); setError(""); }}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {tab === "profile" && (
          <>
            <div style={styles.card}>
              <div style={styles.cardTitle}>Account Information</div>
              <div style={styles.infoRow}><span style={styles.infoLabel}>Email</span><span style={styles.infoValue}>{user?.email}</span></div>
              <div style={styles.infoRow}><span style={styles.infoLabel}>Role</span><span style={styles.infoValue}>{user?.role || "USER"}</span></div>
              <div style={styles.infoRow}><span style={styles.infoLabel}>Trust Score</span><span style={styles.infoValue}>⭐ {user?.trustScore || 0}</span></div>
              <div style={{ ...styles.infoRow, borderBottom: "none" }}><span style={styles.infoLabel}>Verified</span><span style={styles.infoValue}>{user?.isVerified ? "✅ Verified" : "❌ Not verified"}</span></div>
            </div>
            <div style={styles.card}>
              <div style={styles.cardTitle}>Edit Profile</div>
              {message && <div style={styles.successMsg}>{message}</div>}
              {error && <div style={styles.errorMsg}>{error}</div>}
              <form onSubmit={handleSaveProfile}>
                <label style={styles.label}>Full Name</label>
                <input style={styles.input} value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Your full name" required />
                <label style={styles.label}>Phone (Optional)</label>
                <input style={styles.input} value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 (555) 000-0000" />
                <button type="submit" style={styles.saveBtn} disabled={saving}>{saving ? "Saving..." : "Save Changes"}</button>
              </form>
            </div>
            <div style={styles.card}>
              <div style={styles.cardTitle}>Quick Actions</div>
              <div style={styles.quickAction("#d97706")} onClick={() => navigate("/invoices")}>
                <span style={styles.quickActionIcon}>🧾</span>
                <div><div style={styles.quickActionTitle("#d97706")}>Invoices</div><div style={styles.quickActionDesc}>Create secure payment links for anyone</div></div>
              </div>
              <div style={styles.quickAction("#3b82f6")} onClick={() => navigate("/orders")}>
                <span style={styles.quickActionIcon}>📦</span>
                <div><div style={styles.quickActionTitle("#3b82f6")}>My Orders</div><div style={styles.quickActionDesc}>View and manage your orders and offers</div></div>
              </div>
              <div style={styles.quickAction("#16a34a")} onClick={() => navigate("/seller")}>
                <span style={styles.quickActionIcon}>🏪</span>
                <div><div style={styles.quickActionTitle("#16a34a")}>Seller Dashboard</div><div style={styles.quickActionDesc}>Manage your listings and earnings</div></div>
              </div>
              <div style={styles.quickAction("#7c3aed")} onClick={() => navigate("/messages")}>
                <span style={styles.quickActionIcon}>💬</span>
                <div><div style={styles.quickActionTitle("#7c3aed")}>Messages</div><div style={styles.quickActionDesc}>View your conversations</div></div>
              </div>
            </div>
            <div style={styles.card}>
              <button style={styles.signOutBtn} onClick={handleSignOut}>Sign Out</button>
            </div>
          </>
        )}

        {tab === "security" && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Change Password</div>
            {message && <div style={styles.successMsg}>{message}</div>}
            {error && <div style={styles.errorMsg}>{error}</div>}
            <form onSubmit={handleChangePassword}>
              <label style={styles.label}>Current Password</label>
              <input style={styles.input} type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="••••••••" required />
              <label style={styles.label}>New Password</label>
              <input style={styles.input} type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min. 8 characters" required />
              <label style={styles.label}>Confirm New Password</label>
              <input style={styles.input} type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Repeat new password" required />
              <button type="submit" style={styles.saveBtn} disabled={saving}>{saving ? "Changing..." : "Change Password"}</button>
            </form>
          </div>
        )}

        {tab === "notifications" && <NotificationPreferences />}

        {tab === "platform" && (
          <>
            <div style={styles.card}>
              <div style={styles.cardTitle}>Platform Information</div>
              <div style={styles.infoRow}><span style={styles.infoLabel}>Standard Fee</span><span style={styles.infoValue}>2.5%</span></div>
              <div style={styles.infoRow}><span style={styles.infoLabel}>Premium Fee ($5,001+)</span><span style={styles.infoValue}>2.0%</span></div>
              <div style={styles.infoRow}><span style={styles.infoLabel}>Dispute Window</span><span style={styles.infoValue}>5 days after delivery</span></div>
              <div style={{ ...styles.infoRow, borderBottom: "none" }}><span style={styles.infoLabel}>Payout Method</span><span style={styles.infoValue}>Direct to seller account</span></div>
            </div>
            <div style={styles.card}>
              <div style={styles.cardTitle}>How Payments Work</div>
              {[
                { icon: "💳", title: "Buyer pays", desc: "Buyer completes payment through ITrust Secure" },
                { icon: "📦", title: "Seller delivers", desc: "Seller fulfills and submits delivery proof" },
                { icon: "✅", title: "Buyer confirms", desc: "Buyer confirms receipt of item" },
                { icon: "💰", title: "Seller gets paid", desc: "Payment released directly to seller account" },
                { icon: "⚖️", title: "Disputes", desc: "Admin reviews evidence and makes fair decision" },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", gap: 14, padding: "12px 0", borderBottom: i < 4 ? "1px solid rgba(59,130,246,0.08)" : "none" }}>
                  <span style={{ fontSize: 22 }}>{item.icon}</span>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2, color: "#1e293b" }}>{item.title}</div>
                    <div style={{ fontSize: 13, color: "#64748b" }}>{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}